/**
 * Consolidated hook for all address-related operations
 * Combines: useAddressHandlers + useAddressModal + useGoogleAddressValidation
 */

import { useCallback } from 'react';
import { isEqual } from 'lodash';
import * as intrfce from '../Interfaces';
import { createAddressValidationObject, updateSavedAddressWithIdMinus1, validateUSState, validateUSStateExcludingAkHi } from '../utils/addressUtils';
import { isUsAddressUsingExcludedSubdivision } from '../../../constants/productUsStateRestrictions';

interface UseAddressOperationsProps {
  // State
  isBilling: boolean;
  currentStep: number;
  formBillingAddress: intrfce.iAddressFormData;
  formShippingAddress: intrfce.iAddressFormData;
  recommendedBillingAddress: intrfce.iAddressFormData | null;
  recommendedShippingAddress: intrfce.iAddressFormData | null;
  selectedBillingAddressOption: "yourAddress" | "recommendedAddress";
  selectedAddressOption: "yourAddress" | "recommendedAddress";
  selectedBillingAddress: intrfce.iSavedAddress | "reset" | null;
  selectedShippingAddress: intrfce.iSavedAddress | "reset" | null;
  locale: string;
  countryList: any[];
  brand?: string;
  /** Shipping-only: US state codes not allowed for this cart (e.g. category rules). */
  usStateCodesExcludedFromDropdown?: string[];
  
  // Setters
  setFormBillingAddress: React.Dispatch<React.SetStateAction<intrfce.iAddressFormData>>;
  setFormShippingAddress: React.Dispatch<React.SetStateAction<intrfce.iAddressFormData>>;
  setSavedBillingAddress: React.Dispatch<React.SetStateAction<intrfce.iSavedAddress[]>>;
  setSavedShippingAddress: React.Dispatch<React.SetStateAction<intrfce.iSavedAddress[]>>;
  setSelectedBillingAddress: (value: intrfce.iSavedAddress | "reset" | ((prev: intrfce.iSavedAddress | "reset" | null) => intrfce.iSavedAddress | "reset" | null) | null) => void;
  setSelectedShippingAddress: (value: any) => void;
  setRecommendedBillingAddress: (address: intrfce.iAddressFormData | null) => void;
  setRecommendedShippingAddress: (address: intrfce.iAddressFormData | null) => void;
  setBillingAddressDifferences: (diffs: string[]) => void;
  setShippingAddressDifferences: (diffs: string[]) => void;
  setShowAddressModal: (show: boolean) => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<1 | 2>>;
  setSelectedAddressOption: (option: "yourAddress" | "recommendedAddress") => void;
  setValidationErrors: React.Dispatch<React.SetStateAction<any>>;
  setBillingValidationResult: (result: any) => void;
  setShippingValidationResult: (result: any) => void;
  setTouchedFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  
  // Validators
  isBillingFormValid: () => boolean;
  isBillingAddressValid: () => boolean;
  isTaxDataValid: () => boolean;
  isShippingFormValid: (opts?: { dryRun?: boolean }) => boolean;
  isShippingAddressValid: (opts?: { dryRun?: boolean }) => boolean;
  validateForm: (fieldName: string, value: string | {} | boolean, formType: "" | "billing" | "shipping") => boolean;
  
  // Services
  submitAddress: (addressObj: any, onSuccess: (result: any) => void, onError: (error: any) => void) => void;
  showBodyLoader: () => void;
  hideBodyLoader: () => void;
  handleDualAddressSuccess?: (billingResult: any, shippingResult: any) => void;
  t: (key: string) => string;
}

export const useAddressOperations = (props: UseAddressOperationsProps) => {
  const {
    isBilling,
    currentStep,
    formBillingAddress,
    formShippingAddress,
    recommendedBillingAddress,
    recommendedShippingAddress,
    selectedBillingAddressOption,
    selectedAddressOption,
    selectedBillingAddress,
    selectedShippingAddress,
    locale,
    countryList,
    brand,
    usStateCodesExcludedFromDropdown = [],
    setFormBillingAddress,
    setFormShippingAddress,
    setSavedBillingAddress,
    setSavedShippingAddress,
    setSelectedBillingAddress,
    setSelectedShippingAddress,
    setRecommendedBillingAddress,
    setRecommendedShippingAddress,
    setBillingAddressDifferences,
    setShippingAddressDifferences,
    setShowAddressModal,
    setCurrentStep,
    setSelectedAddressOption,
    setValidationErrors,
    setBillingValidationResult,
    setShippingValidationResult,
    setTouchedFields,
    isBillingFormValid,
    isBillingAddressValid,
    isTaxDataValid,
    isShippingFormValid,
    isShippingAddressValid,
    validateForm,
    submitAddress,
    showBodyLoader,
    hideBodyLoader,
    handleDualAddressSuccess,
    t
  } = props;

  const isAkHiRestrictedPartner = (brandKey: string | undefined): boolean => {
    const key = (brandKey || "").trim().toUpperCase();
    return key === "MSI" || key === "IBUYPOWER";
  };

  /**
   * Creates address entry from form data
   */
  const createAddressEntry = useCallback((
    addressData: intrfce.iAddressFormData,
    nickName: string
  ): intrfce.iSavedAddress => {
    return {
      id: addressData.id || 0,
      city: addressData.cityValue || "",
      country: addressData.countryValue || "US",
      countryName: addressData.countryName || "United States",
      countrySubdivision: addressData.stateValue || "",
      firstName: addressData.firstName || "",
      lastName: addressData.lastName || "",
      attn: addressData.attn || "",
      line1: addressData.address || "",
      line2: addressData.line2 || "",
      nickName,
      phoneNumber: addressData.contact || "",
      postalCode: addressData.pincode || "",
      isDefault: false,
    };
  }, []);

  /**
   * Updates the billing address with suggested/recommended address
   */
  const updateSuggestedBillingAddress = useCallback((suggestedAddr: intrfce.iSavedAddress) => {
    console.log("updateSuggestedBillingAddress==>", suggestedAddr);

    // Validate each field
    Object.keys(suggestedAddr).forEach((fieldName) => {
      const value = suggestedAddr[fieldName as keyof intrfce.iSavedAddress];
      if (value && typeof value === 'string' && value.length) {
        validateForm(fieldName, value, "billing");
      }
    });

    // Update savedBillingAddress - update or create id === -1 entry (New Address)
    setSavedBillingAddress((prev) => updateSavedAddressWithIdMinus1(prev, suggestedAddr));

    // Update form data with the same address format, preserving contact fields (email, vemail)
    setFormBillingAddress((prevData) => ({
      ...prevData,
      // Address fields
      pincode: suggestedAddr.postalCode || prevData.pincode || "",
      address: suggestedAddr.line1 || prevData.address || "",
      line2: suggestedAddr.line2 || prevData.line2 || "",
      cityValue: suggestedAddr.city || prevData.cityValue || "",
      stateValue: suggestedAddr.countrySubdivision || prevData.stateValue || "",
      countryName: suggestedAddr.countryName || prevData.countryName || "",
      countryValue: suggestedAddr.country || prevData.countryValue || "",
      // Contact fields - preserve email and vemail, update others if provided
      firstName: suggestedAddr.firstName || prevData.firstName || "",
      lastName: suggestedAddr.lastName || prevData.lastName || "",
      attn: suggestedAddr.attn || prevData.attn || "",
      contact: suggestedAddr.phoneNumber || prevData.contact || "",
      // Email and vemail are preserved from prevData (not in suggestedAddr)
      email: prevData.email || "",
      vemail: prevData.vemail || "",
      // Set id to -1 for new/recommended address
      id: -1,
    }));

    // Update selectedBillingAddress with id === -1
    const addressWithId = { ...suggestedAddr, id: -1 };
    setSelectedBillingAddress(addressWithId);
  }, [validateForm, setSavedBillingAddress, setFormBillingAddress, setSelectedBillingAddress]);

  /**
   * Updates the shipping address with suggested/recommended address
   */
  const updateSuggestedShippingAddress = useCallback((suggestedAddr: intrfce.iSavedAddress) => {
    console.log("updateSuggestedShippingAddress==>", suggestedAddr);

    // Validate each field
    Object.keys(suggestedAddr).forEach((fieldName) => {
      const value = suggestedAddr[fieldName as keyof intrfce.iSavedAddress];
      if (value && typeof value === 'string' && value.length) {
        validateForm(fieldName, value, "shipping");
      }
    });

    // Update savedShippingAddress - update or create id === -1 entry (New Address)
    setSavedShippingAddress((prev) => updateSavedAddressWithIdMinus1(prev, suggestedAddr));

    // Update form data with the same address format, preserving contact fields (email, vemail if they exist)
    setFormShippingAddress((prev) => ({
      ...prev,
      // Address fields
      pincode: suggestedAddr.postalCode || prev?.pincode || "",
      address: suggestedAddr.line1 || prev?.address || "",
      line2: suggestedAddr.line2 || prev?.line2 || "",
      cityValue: suggestedAddr.city || prev?.cityValue || "",
      stateValue: suggestedAddr.countrySubdivision || prev?.stateValue || "",
      countryName: suggestedAddr.countryName || prev?.countryName || "",
      countryValue: suggestedAddr.country || prev?.countryValue || "",
      // Contact fields - preserve email and vemail if they exist, update others if provided
      firstName: suggestedAddr.firstName || prev?.firstName || "",
      lastName: suggestedAddr.lastName || prev?.lastName || "",
      attn: (suggestedAddr.attn && suggestedAddr.attn.trim() !== "") ? suggestedAddr.attn : (prev?.attn && prev.attn.trim() !== "" ? prev?.attn : ""),
      contact: suggestedAddr.phoneNumber || prev?.contact || "",
      // Email and vemail are preserved from prev (not in suggestedAddr)
      email: prev?.email || "",
      vemail: prev?.vemail || "",
      // Set id to -1 for new/recommended address
      id: -1,
    }));
    
    // Update selectedShippingAddress with id === -1
    const addressWithId = { ...suggestedAddr, id: -1 };
    setSelectedShippingAddress(addressWithId);
  }, [validateForm, setSavedShippingAddress, setFormShippingAddress, setSelectedShippingAddress]);

  /**
   * Updates recommended address after validation
   * Enhanced version that handles subpremise components and detailed address parsing
   */
  const updateRecommendedAddress = useCallback((
    result: any,
    formAddress: intrfce.iAddressFormData,
    addressType: "shipping" | "billing",
    setRecommendedAddress: (address: intrfce.iAddressFormData | null) => void,
    setValidationResult: (result: any) => void,
    setDifferences: (diffs: string[]) => void
  ) => {
    console.log(`updateRecommendedAddress - ${addressType}:`, result);

    if (!result) {
      setRecommendedAddress(null);
      setValidationResult(null);
      setDifferences([]);
      return;
    }

    setValidationResult(result);

    if (!result.result?.address?.postalAddress) {
      setRecommendedAddress(null);
      setDifferences([]);
      return;
    }

    const postalAddress = result.result.address.postalAddress;
    const addressComponents = result.result.address.addressComponents || [];

    // Extract address lines with subpremise handling
    console.log(`[${addressType}] Google addressLines:`, postalAddress.addressLines);
    let line1 = '';
    let line2 = '';

    if (postalAddress.addressLines && postalAddress.addressLines.length > 0) {
      line1 = postalAddress.addressLines[0] || '';
      line2 = postalAddress.addressLines.length > 1 ? postalAddress.addressLines[1] || '' : '';

      // If there's no line2 but there's a subpremise component, extract it for line2
      if (!line2 && addressComponents.length > 0) {
        const subpremiseComponent = addressComponents.find((c: any) => c.componentType === 'subpremise');
        if (subpremiseComponent) {
          const subpremiseText = subpremiseComponent.componentName?.text;
          if (subpremiseText) {
            // Remove the subpremise from line1 (it might be at the end with #, -, etc.)
            const patterns = [
              new RegExp(`\\s*#${subpremiseText}$`, 'i'),
              new RegExp(`\\s*-${subpremiseText}$`, 'i'),
              new RegExp(`\\s*${subpremiseText}$`, 'i'),
              new RegExp(`\\s*#\\s*${subpremiseText}$`, 'i'),
            ];

            for (const pattern of patterns) {
              if (pattern.test(line1)) {
                line1 = line1.replace(pattern, '').trim();
                line2 = `#${subpremiseText}`;
                break;
              }
            }
          }
        }
      }

      console.log(`[${addressType}] Extracted - line1: "${line1}", line2: "${line2}"`);
    }

    // Get country name from regionCode using the countryList
    const regionCode = postalAddress.regionCode || "US";
    const countryInfo = countryList.find((c: any) => c.ISO_CC === regionCode);
    const countryName = countryInfo?.countryName || formAddress.countryName || '';

    const addressData: intrfce.iAddressFormData = {
      pincode: postalAddress.postalCode || '',
      address: line1,
      line2: line2,
      cityValue: postalAddress.locality || '',
      stateValue: postalAddress.administrativeArea || '',
      countryName: countryName,
      countryValue: regionCode,
      county: locale !== "en-us" ? postalAddress.sublocality || "" : undefined,
    };

    console.log(`${addressType} - Google addressData:`, addressData);
    console.log(`${addressType} - Form address:`, formAddress);

    // Check if addressData matches the form address (normalize empty strings vs undefined)
    const normalize = (val: any) => val || '';
    const differences: string[] = [];

    if (normalize(addressData.pincode) !== normalize(formAddress.pincode))
      differences.push("pincode");
    if (normalize(addressData.address) !== normalize(formAddress.address))
      differences.push("address");
    if (normalize(addressData.line2) !== normalize(formAddress.line2))
      differences.push("line2");
    if (normalize(addressData.cityValue) !== normalize(formAddress.cityValue))
      differences.push("cityValue");
    if (normalize(addressData.stateValue) !== normalize(formAddress.stateValue))
      differences.push("stateValue");
    if (normalize(addressData.countryName) !== normalize(formAddress.countryName))
      differences.push("countryName");
    if (normalize(addressData.countryValue) !== normalize(formAddress.countryValue))
      differences.push("countryValue");

    console.log(`${addressType} - Differences:`, differences);

    // Store differences for highlighting in modal
    setDifferences(differences);

    // Check if addresses are equal (ignoring case and whitespace)
    const addressesEqual = isEqual(
      {
        address: formAddress.address?.toLowerCase().trim(),
        city: formAddress.cityValue?.toLowerCase().trim(),
        state: formAddress.stateValue?.toLowerCase().trim(),
        zip: formAddress.pincode?.toLowerCase().trim(),
        country: formAddress.countryValue?.toLowerCase().trim()
      },
      {
        address: addressData.address?.toLowerCase().trim(),
        city: addressData.cityValue?.toLowerCase().trim(),
        state: addressData.stateValue?.toLowerCase().trim(),
        zip: addressData.pincode?.toLowerCase().trim(),
        country: addressData.countryValue?.toLowerCase().trim()
      }
    );

    if (addressesEqual) {
      setRecommendedAddress(null);
    } else if (
      addressType === "shipping" &&
      isUsAddressUsingExcludedSubdivision(
        addressData.countryValue,
        addressData.stateValue,
        usStateCodesExcludedFromDropdown
      )
    ) {
      // Google's canonical match is a US state we cannot ship to for this cart — do not offer it as recommended
      setRecommendedAddress(null);
    } else {
      // For all brands, validate state before showing recommended addresses
      if (brand) {
        const stateValidation = isAkHiRestrictedPartner(brand)
          ? validateUSStateExcludingAkHi(addressData)
          : validateUSState(addressData);
        
        if (stateValidation.isValid) {
          setRecommendedAddress(addressData);
        } else {
          setRecommendedAddress(null);
        }
      } else {
        // No brand specified, show recommended address
        setRecommendedAddress(addressData);
      }
    }
  }, [countryList, locale, brand, usStateCodesExcludedFromDropdown]);

  /**
   * Handles address confirmation from modal
   */
  const handleConfirmAddress = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    // For all brands, validate shipping and billing address states before proceeding
    if (brand) {
      // Validate shipping address (use recommended if selected, otherwise use form address)
      const shippingAddressToValidate = (selectedAddressOption === "recommendedAddress" && recommendedShippingAddress) 
        ? recommendedShippingAddress 
        : formShippingAddress;
      
      const shippingStateValidation = isAkHiRestrictedPartner(brand)
        ? validateUSStateExcludingAkHi(shippingAddressToValidate)
        : validateUSState(shippingAddressToValidate);
      
      if (!shippingStateValidation.isValid) {
        setValidationErrors((prevErrors: any) => ({
          ...prevErrors,
          shippingaddress: shippingStateValidation.errorMessage || 'We cannot ship to this state. Please use a different shipping address.',
        }));
        return; // Block proceeding
      }
      
      // Also validate billing address if different
      if (isBilling) {
        const billingAddressToValidate = (selectedBillingAddressOption === "recommendedAddress" && recommendedBillingAddress)
          ? recommendedBillingAddress
          : formBillingAddress;
        
        const billingStateValidation = isAkHiRestrictedPartner(brand)
          ? validateUSStateExcludingAkHi(billingAddressToValidate)
          : validateUSState(billingAddressToValidate);
        
        if (!billingStateValidation.isValid) {
          setValidationErrors((prevErrors: any) => ({
            ...prevErrors,
            billingaddress: billingStateValidation.errorMessage || 'We cannot bill to this state. Please use a different billing address.',
          }));
          return; // Block proceeding
        }
      }
    }
    
    const proceedToStep2 = sessionStorage.getItem('proceedToStep2');
    
      if (proceedToStep2 === 'true') {
      // Apply selected addresses before proceeding to Step 2
      if (isBilling) {
        // Dual address mode
        if (selectedBillingAddressOption === "recommendedAddress" && recommendedBillingAddress) {
          // Merge recommended address with current form to preserve firstName/lastName
          const billingAddressWithContact = {
            ...recommendedBillingAddress,
            firstName: recommendedBillingAddress.firstName || formBillingAddress.firstName || "",
            lastName: recommendedBillingAddress.lastName || formBillingAddress.lastName || "",
            contact: recommendedBillingAddress.contact || formBillingAddress.contact || "",
            email: recommendedBillingAddress.email || formBillingAddress.email || "",
            vemail: recommendedBillingAddress.vemail || formBillingAddress.vemail || "",
          };
          const billingAddressEntry = createAddressEntry(
            billingAddressWithContact,
            "Recommended Billing Address"
          );
          updateSuggestedBillingAddress(billingAddressEntry);
        } else if (selectedBillingAddressOption === "yourAddress") {
          const billingAddressEntry = createAddressEntry(
            formBillingAddress,
            "Billing Address"
          );
          updateSuggestedBillingAddress(billingAddressEntry);
        }
        
        if (selectedAddressOption === "recommendedAddress" && recommendedShippingAddress) {
          // Merge recommended address with current form to preserve firstName/lastName
          const shippingAddressWithContact = {
            ...recommendedShippingAddress,
            firstName: recommendedShippingAddress.firstName || formShippingAddress.firstName || "",
            lastName: recommendedShippingAddress.lastName || formShippingAddress.lastName || "",
            contact: recommendedShippingAddress.contact || formShippingAddress.contact || "",
            email: recommendedShippingAddress.email || formShippingAddress.email || "",
            vemail: recommendedShippingAddress.vemail || formShippingAddress.vemail || "",
            attn: recommendedShippingAddress.attn || formShippingAddress.attn || "",
          };
          const shippingAddressEntry = createAddressEntry(
            shippingAddressWithContact,
            "Recommended Shipping Address"
          );
          updateSuggestedShippingAddress(shippingAddressEntry);
        } else if (selectedAddressOption === "yourAddress") {
          const shippingAddressEntry = createAddressEntry(
            formShippingAddress,
            "Shipping Address"
          );
          updateSuggestedShippingAddress(shippingAddressEntry);
        }
      } else {
        // Single address mode - shipping is collected first, so use formShippingAddress
        const address = selectedAddressOption === "recommendedAddress" && recommendedShippingAddress
          ? {
              ...recommendedShippingAddress,
              // Preserve firstName/lastName from selected saved address or current form
              firstName: recommendedShippingAddress.firstName || 
                ((typeof selectedShippingAddress !== "string" && selectedShippingAddress?.firstName) || "") ||
                formShippingAddress.firstName || "",
              lastName: recommendedShippingAddress.lastName || 
                ((typeof selectedShippingAddress !== "string" && selectedShippingAddress?.lastName) || "") ||
                formShippingAddress.lastName || "",
              contact: recommendedShippingAddress.contact || 
                ((typeof selectedShippingAddress !== "string" && selectedShippingAddress?.phoneNumber) || "") ||
                formShippingAddress.contact || "",
              email: recommendedShippingAddress.email || formShippingAddress.email || "",
              vemail: recommendedShippingAddress.vemail || formShippingAddress.vemail || "",
              attn: recommendedShippingAddress.attn || 
                ((typeof selectedShippingAddress !== "string" && selectedShippingAddress?.attn) || "") ||
                formShippingAddress.attn || "",
            }
          : formShippingAddress;
        const nickName = selectedAddressOption === "recommendedAddress"
          ? "Recommended Address"
          : "Shipping Address";
        
        const addressEntry = createAddressEntry(address, nickName);
        updateSuggestedBillingAddress(addressEntry);
        updateSuggestedShippingAddress(addressEntry);
      }
      
      sessionStorage.removeItem('proceedToStep2');
      setShowAddressModal(false);
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // On Step 1, apply addresses and close modal
    if (currentStep === 1) {
      if (isBilling) {
        if (selectedBillingAddressOption === "recommendedAddress" && recommendedBillingAddress) {
          // Merge recommended address with current form to preserve firstName/lastName
          // Also check selectedBillingAddress if it's a saved address (has firstName/lastName)
          const savedBillingFirstName = (typeof selectedBillingAddress !== "string" && selectedBillingAddress?.firstName) || "";
          const savedBillingLastName = (typeof selectedBillingAddress !== "string" && selectedBillingAddress?.lastName) || "";
          const savedBillingContact = (typeof selectedBillingAddress !== "string" && selectedBillingAddress?.phoneNumber) || "";
          
          const billingAddressWithContact = {
            ...recommendedBillingAddress,
            firstName: recommendedBillingAddress.firstName || savedBillingFirstName || formBillingAddress.firstName || "",
            lastName: recommendedBillingAddress.lastName || savedBillingLastName || formBillingAddress.lastName || "",
            contact: recommendedBillingAddress.contact || savedBillingContact || formBillingAddress.contact || "",
            email: recommendedBillingAddress.email || formBillingAddress.email || "",
            vemail: recommendedBillingAddress.vemail || formBillingAddress.vemail || "",
          };
          const billingAddressEntry = createAddressEntry(
            billingAddressWithContact,
            "Recommended Billing Address"
          );
          updateSuggestedBillingAddress(billingAddressEntry);
        } else if (selectedBillingAddressOption === "yourAddress") {
          const billingAddressEntry = createAddressEntry(
            formBillingAddress,
            "Billing Address"
          );
          updateSuggestedBillingAddress(billingAddressEntry);
        }
        
        if (selectedAddressOption === "recommendedAddress" && recommendedShippingAddress) {
          // Merge recommended address with current form to preserve firstName/lastName
          // Also check selectedShippingAddress if it's a saved address (has firstName/lastName)
          const savedShippingFirstName = (typeof selectedShippingAddress !== "string" && selectedShippingAddress?.firstName) || "";
          const savedShippingLastName = (typeof selectedShippingAddress !== "string" && selectedShippingAddress?.lastName) || "";
          const savedShippingContact = (typeof selectedShippingAddress !== "string" && selectedShippingAddress?.phoneNumber) || "";
          const savedShippingAttn = (typeof selectedShippingAddress !== "string" && selectedShippingAddress?.attn) || "";
          
          const shippingAddressWithContact = {
            ...recommendedShippingAddress,
            firstName: recommendedShippingAddress.firstName || savedShippingFirstName || formShippingAddress.firstName || "",
            lastName: recommendedShippingAddress.lastName || savedShippingLastName || formShippingAddress.lastName || "",
            contact: recommendedShippingAddress.contact || savedShippingContact || formShippingAddress.contact || "",
            email: recommendedShippingAddress.email || formShippingAddress.email || "",
            vemail: recommendedShippingAddress.vemail || formShippingAddress.vemail || "",
            attn: recommendedShippingAddress.attn || savedShippingAttn || formShippingAddress.attn || "",
          };
          const shippingAddressEntry = createAddressEntry(
            shippingAddressWithContact,
            "Recommended Shipping Address"
          );
          updateSuggestedShippingAddress(shippingAddressEntry);
        } else if (selectedAddressOption === "yourAddress") {
          const shippingAddressEntry = createAddressEntry(
            formShippingAddress,
            "Shipping Address"
          );
          updateSuggestedShippingAddress(shippingAddressEntry);
        }
      } else {
        const address = selectedAddressOption === "recommendedAddress" && recommendedShippingAddress
          ? {
              ...recommendedShippingAddress,
              // Preserve firstName/lastName from selected saved address or current form
              firstName: recommendedShippingAddress.firstName || 
                ((typeof selectedShippingAddress !== "string" && selectedShippingAddress?.firstName) || "") ||
                formShippingAddress.firstName || "",
              lastName: recommendedShippingAddress.lastName || 
                ((typeof selectedShippingAddress !== "string" && selectedShippingAddress?.lastName) || "") ||
                formShippingAddress.lastName || "",
              contact: recommendedShippingAddress.contact || 
                ((typeof selectedShippingAddress !== "string" && selectedShippingAddress?.phoneNumber) || "") ||
                formShippingAddress.contact || "",
              email: recommendedShippingAddress.email || formShippingAddress.email || "",
              vemail: recommendedShippingAddress.vemail || formShippingAddress.vemail || "",
              attn: recommendedShippingAddress.attn || 
                ((typeof selectedShippingAddress !== "string" && selectedShippingAddress?.attn) || "") ||
                formShippingAddress.attn || "",
            }
          : formBillingAddress;
        const nickName = selectedAddressOption === "recommendedAddress"
          ? "Recommended Address"
          : "Billing Address";
        
        const addressEntry = createAddressEntry(address, nickName);
        updateSuggestedBillingAddress(addressEntry);
        updateSuggestedShippingAddress(addressEntry);
      }
      
      setShowAddressModal(false);
      return;
    }
    
    // Default: close modal
    setShowAddressModal(false);
  }, [
    isBilling,
    currentStep,
    formBillingAddress,
    formShippingAddress,
    recommendedBillingAddress,
    recommendedShippingAddress,
    selectedBillingAddressOption,
    selectedAddressOption,
    selectedBillingAddress,
    selectedShippingAddress,
    createAddressEntry,
    updateSuggestedBillingAddress,
    updateSuggestedShippingAddress,
    setShowAddressModal,
    setCurrentStep,
    brand,
    setValidationErrors
  ]);

  /**
   * Handles shipping address update (Edit Address button)
   */
  const handleShippingAddressUpdate = useCallback((
    ev: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (isBilling) {
      // Dual mode: update based on selection
      if (selectedBillingAddressOption === "yourAddress") {
        const updatedBillingAddress = createAddressEntry(
          formBillingAddress,
          "Billing Address"
        );
        updateSuggestedBillingAddress(updatedBillingAddress);
      }
      
      if (selectedAddressOption === "yourAddress") {
        const updatedShippingAddress = createAddressEntry(
          formShippingAddress,
          "Order Address"
        );
        updateSuggestedShippingAddress(updatedShippingAddress);
      }
    } else {
      // Single mode: update billing
      const updatedBillingAddress = createAddressEntry(
        formBillingAddress,
        "Billing Address"
      );
      updateSuggestedBillingAddress(updatedBillingAddress);
    }
    
    setShowAddressModal(false);
  }, [
    isBilling,
    selectedBillingAddressOption,
    selectedAddressOption,
    formBillingAddress,
    formShippingAddress,
    createAddressEntry,
    updateSuggestedBillingAddress,
    updateSuggestedShippingAddress,
    setShowAddressModal
  ]);

  /**
   * Handles address option change in modal
   */
  const handleAddressOptionChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement>,
    option: string
  ) => {
    setSelectedAddressOption(option as "yourAddress" | "recommendedAddress");
  }, [setSelectedAddressOption]);

  /**
   * Handles continue to payment (Step 1 -> Step 2)
   */
  const handleContinueToPayment = useCallback(async () => {
    // Mark all fields as touched before validation so errors are shown for all invalid fields
    setTouchedFields((prev) => {
      const allFields = new Set(prev);
      // Shipping fields
      allFields.add('firstNameShiping');
      allFields.add('lastNameShiping');
      allFields.add('contactShiping');
      allFields.add('emailShiping');
      allFields.add('vemailShiping');
      allFields.add('addressShipping');
      allFields.add('pincode');
      allFields.add('cityValue');
      allFields.add('stateValue');
      // Billing fields
      allFields.add('firstNameBilling');
      allFields.add('lastNameBilling');
      allFields.add('contactBilling');
      allFields.add('addressBilling');
      allFields.add('companyName');
      allFields.add('vatNo');
      return allFields;
    });

    // Shipping is always required (shown first as primary address)
    const shippingValid = isShippingFormValid() && isShippingAddressValid();
    // Billing is only required if different from shipping (checkbox checked)
    const billingValid = !isBilling || (isBillingFormValid() && isBillingAddressValid() && isTaxDataValid());
    
    // For all brands, validate shipping and billing address states before proceeding
    if (brand) {
      const shippingStateValidation = isAkHiRestrictedPartner(brand)
        ? validateUSStateExcludingAkHi(formShippingAddress)
        : validateUSState(formShippingAddress);
      
      if (!shippingStateValidation.isValid) {
        setValidationErrors((prevErrors: any) => ({
          ...prevErrors,
          shippingaddress: shippingStateValidation.errorMessage || 'We cannot ship to this state. Please use a different shipping address.',
        }));
        return; // Block proceeding
      }
      
      // Also validate billing address if different
      if (isBilling) {
        const billingStateValidation = isAkHiRestrictedPartner(brand)
          ? validateUSStateExcludingAkHi(formBillingAddress)
          : validateUSState(formBillingAddress);
        
        if (!billingStateValidation.isValid) {
          setValidationErrors((prevErrors: any) => ({
            ...prevErrors,
            billingaddress: billingStateValidation.errorMessage || 'We cannot bill to this state. Please use a different billing address.',
          }));
          return; // Block proceeding
        }
      }
    }
    
    if (shippingValid && billingValid) {
      // Copy shipping email to billing email before proceeding
      if (formShippingAddress?.email) {
        setFormBillingAddress((prev) => ({
          ...prev,
          email: formShippingAddress.email || prev?.email || "",
          vemail: formShippingAddress.vemail || prev?.vemail || "",
        }));
      }

      showBodyLoader();
      sessionStorage.setItem('proceedToStep2', 'true');
      
      const invalidateRegions = ['BG', 'HR', 'CY', 'EE', 'GR', 'HU', 'LT', 'MT', 'RO', 'SI'];
      
      const billingAddressObj = createAddressValidationObject(
        false,
        formShippingAddress,
        formBillingAddress,
        locale
      );
      
      try {
        let billingResult: any = null;
        if (billingAddressObj?.regionCode && !invalidateRegions.includes(billingAddressObj?.regionCode)) {
          billingResult = await new Promise((resolve) => {
            submitAddress(
              billingAddressObj,
              (result) => resolve(result),
              (err) => { 
                console.log("Billing validation error:", err); 
                resolve(null); 
              }
            );
          });
        }
        
        let shippingResult: any = null;
        if (isBilling) {
          const shippingAddressObj = createAddressValidationObject(
            true,
            formShippingAddress,
            formBillingAddress,
            locale
          );
          
          if (shippingAddressObj?.regionCode && !invalidateRegions.includes(shippingAddressObj?.regionCode)) {
            shippingResult = await new Promise((resolve) => {
              submitAddress(
                shippingAddressObj,
                (result) => resolve(result),
                (err) => { 
                  console.log("Shipping validation error:", err); 
                  resolve(null); 
                }
              );
            });
          }
        }
        
        // Update recommended addresses and show modal if needed
        updateRecommendedAddress(
          billingResult,
          formBillingAddress,
          "billing",
          setRecommendedBillingAddress,
          setBillingValidationResult,
          setBillingAddressDifferences
        );

        if (isBilling) {
          updateRecommendedAddress(
            shippingResult,
            formShippingAddress,
            "shipping",
            setRecommendedShippingAddress,
            setShippingValidationResult,
            setShippingAddressDifferences
          );
        } else {
          // If addresses are the same, use billing result for shipping too
          updateRecommendedAddress(
            billingResult,
            formShippingAddress,
            "shipping",
            setRecommendedShippingAddress,
            setShippingValidationResult,
            setShippingAddressDifferences
          );
        }

        // Check if we need to show the modal (if there are recommended addresses)
        const hasRecommendedBilling = billingResult?.result?.address?.postalAddress;
        const hasRecommendedShipping = isBilling
          ? shippingResult?.result?.address?.postalAddress
          : billingResult?.result?.address?.postalAddress;

        if (hasRecommendedBilling || hasRecommendedShipping) {
          hideBodyLoader();
          setShowAddressModal(true);
        } else {
          // No recommended addresses, proceed to step 2
          hideBodyLoader();
          setCurrentStep(2);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        // Also call handleDualAddressSuccess if provided (for backward compatibility)
        handleDualAddressSuccess?.(billingResult, shippingResult);
      } catch (error) {
        console.log("Address validation error:", error);
        hideBodyLoader();
        setShowAddressModal(true);
      }
    } else {
      setValidationErrors((prev: any) => ({
        ...prev,
        ...(!shippingValid && { shippingAddress: t('PLEASE_COMPLETE_SHIPPING_ADDRESS') }),
        ...(!billingValid && { billingAddress: t('PLEASE_COMPLETE_BILLING_ADDRESS') })
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isBillingFormValid,
    isBillingAddressValid,
    isTaxDataValid,
    isBilling,
    isShippingFormValid,
    isShippingAddressValid,
    formShippingAddress,
    formBillingAddress,
    locale,
    submitAddress,
    handleDualAddressSuccess,
    brand,
    setValidationErrors,
    showBodyLoader,
    hideBodyLoader,
    setShowAddressModal,
    setValidationErrors,
    setTouchedFields,
    setFormBillingAddress,
    t
  ]);

  /**
   * Handles back to addresses (Step 2 -> Step 1)
   * Note: No fetchCart call needed here since we're just navigating between steps
   * without submitting any data to the backend
   */
  const handleBackToAddresses = useCallback(() => {
    setCurrentStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setCurrentStep]);

  /**
   * Validates both billing and shipping addresses
   */
  const validateAddresses = useCallback(async (): Promise<{
    billingResult: any;
    shippingResult: any;
  }> => {
    showBodyLoader();

    const invalidateRegions = ['BG', 'HR', 'CY', 'EE', 'GR', 'HU', 'LT', 'MT', 'RO', 'SI'];

    // Create address validation objects
    const billingAddressObj = createAddressValidationObject(
      false,
      formShippingAddress,
      formBillingAddress,
      locale
    );

    let billingResult: any = null;
    let shippingResult: any = null;

    try {
      // Validate billing address
      if (billingAddressObj?.regionCode && !invalidateRegions.includes(billingAddressObj?.regionCode)) {
        billingResult = await new Promise((resolve) => {
          submitAddress(
            billingAddressObj,
            (result) => resolve(result),
            (err) => {
              console.log("Billing validation error:", err);
              resolve(null);
            }
          );
        });
      }

      // Validate shipping address (if different from billing)
      if (isBilling) {
        const shippingAddressObj = createAddressValidationObject(
          true,
          formShippingAddress,
          formBillingAddress,
          locale
        );

        if (shippingAddressObj?.regionCode && !invalidateRegions.includes(shippingAddressObj?.regionCode)) {
          shippingResult = await new Promise((resolve) => {
            submitAddress(
              shippingAddressObj,
              (result) => resolve(result),
              (err) => {
                console.log("Shipping validation error:", err);
                resolve(null);
              }
            );
          });
        }
      }

      // Update recommended addresses
      updateRecommendedAddress(
        billingResult,
        formBillingAddress,
        "billing",
        setRecommendedBillingAddress,
        setBillingValidationResult,
        setBillingAddressDifferences
      );

      if (isBilling) {
        updateRecommendedAddress(
          shippingResult,
          formShippingAddress,
          "shipping",
          setRecommendedShippingAddress,
          setShippingValidationResult,
          setShippingAddressDifferences
        );
      }

      hideBodyLoader();

      return { billingResult, shippingResult };
    } catch (error) {
      console.log("Address validation error:", error);
      hideBodyLoader();
      throw error;
    }
  }, [
    formBillingAddress,
    formShippingAddress,
    isBilling,
    locale,
    submitAddress,
    updateRecommendedAddress,
    showBodyLoader,
    hideBodyLoader,
    setRecommendedBillingAddress,
    setRecommendedShippingAddress,
    setBillingValidationResult,
    setShippingValidationResult,
    setBillingAddressDifferences,
    setShippingAddressDifferences
  ]);

  return {
    // Address handlers
    updateSuggestedBillingAddress,
    updateSuggestedShippingAddress,
    updateRecommendedAddress,
    
    // Modal handlers
    handleConfirmAddress,
    handleShippingAddressUpdate,
    handleAddressOptionChange,
    handleContinueToPayment,
    handleBackToAddresses,
    
    // Validation
    validateAddresses,
    
    // Utilities
    createAddressEntry
  };
};
